import { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";

export const LEAD_UNLOCK_COST = 100;
export const DEPOSIT_COLLECTION_COST = 30;
export const INVOICE_SEND_COST = 20;
export const DEBT_COLLECTOR_COST = 80;
export const MAX_LEAD_BUYERS = 3;

// SECURITY: businessId must come from useAuth() session, never from URL params or user input.
// Verified in useAuth.js — business_id is read from profiles using session.user.id as the filter.
export function useCredits({ businessId, onLeadUnlocked, showToast = () => {} }) {
  const [creditBalance, setCreditBalance] = useState(0);
  const [unlockedLeadIds, setUnlockedLeadIds] = useState([]);

  useEffect(() => {
    if (!businessId) return;

    async function loadWalletAndUnlocks() {
      const { data: walletData, error: walletError } = await supabase
        .from("credit_wallets")
        .select("*")
        .eq("business_id", businessId)
        .maybeSingle();

      if (!walletError && walletData) {
        setCreditBalance(walletData.balance || 0);
      }

      const { data: unlockData, error: unlockError } = await supabase
        .from("lead_unlocks")
        .select("lead_id")
        .eq("business_id", businessId);

      if (!unlockError) {
        setUnlockedLeadIds((unlockData || []).map((row) => row.lead_id));
      }
    }

    loadWalletAndUnlocks();
  }, [businessId]);

  async function unlockLead(leadId) {
    if (!businessId) {
      showToast("No business is loaded.", "error");
      return;
    }

    if (unlockedLeadIds.includes(leadId)) return;

    if (creditBalance < LEAD_UNLOCK_COST) {
      showToast("Not enough credits.", "error");
      return;
    }

    try {
      const { data: rpcData, error } = await supabase.rpc("unlock_lead_atomic", {
        p_lead_id: leadId,
        p_credit_cost: LEAD_UNLOCK_COST,
      });

      if (error) {
        const msg = error.message || "";
        if (msg.includes("Already unlocked")) {
          showToast("Your business has already unlocked this lead.", "info");
        } else if (msg.includes("sold out")) {
          showToast("This lead has already been sold to the maximum number of businesses.", "info");
        } else if (msg.includes("Insufficient credits")) {
          showToast("Not enough credits.", "error");
        } else if (msg.includes("Wallet not found")) {
          showToast("Credit wallet not found. Please contact support.", "error");
        } else {
          showToast(`Failed to unlock lead: ${msg}`, "error");
        }
        return;
      }

      setUnlockedLeadIds((prev) => [...prev, leadId]);
      // Use server-confirmed balance if returned, otherwise decrement locally
      if (rpcData?.balance != null) {
        setCreditBalance(rpcData.balance);
      } else {
        setCreditBalance((prev) => prev - LEAD_UNLOCK_COST);
      }
      onLeadUnlocked?.(leadId);
      if (rpcData?.already_unlocked) {
        showToast("Lead already unlocked — contact details are now visible.", "info");
      } else {
        showToast("Lead unlocked. You can now view full contact details.", "success");
      }
    } catch (err) {
      showToast(`Failed to unlock lead: ${err.message || "Unknown error"}`, "error");
    }
  }

  return { creditBalance, unlockedLeadIds, unlockLead };
}
